import React from 'react';
import { Button, Link } from '@americanexpress/dls-react';
import { useRouteParams } from '@/context/routeParamsContext';
import {
  centurionLoungeEligibleCards,
  waitlistStatus,
  WAITLIST_SCROLL_POSITION_KEY,
} from '@/constants/waitlistLounges';
import { useSelectedProductFromUserWallet } from '@/hooks/memberHooks';
import { useUserWaitListDetails } from '@/hooks/waitListHooks/waitListHelperHooks';
import { locales } from '@/constants/supportedLocales';
import { useSelector } from 'react-redux';
import { getIsLoggedIn } from '@/selectors/authSelectors';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useModal } from '@/context/modalContext';
import {
  useIsWaitListEnabledLounge,
  useLoungeById,
} from '@/hooks/loungesHooks/loungesHelperHooks';
import * as loungeHelper from '@/helpers/loungeHelper';
import { useRouter } from '@americanexpress/one-app-router';
import { IconTime } from '@americanexpress/dls-icons';
import {
  cn,
  saveScrollBeforeExternalNavigation,
  getBasePathFromLocation,
  addTrailingSlashIfMissing,
} from '@/helpers/utils';
import { RouteParams } from '@/types';
import styles from './WaitListButton.module.scss';
import featuredStyles from './FeaturedLoungeWaitListInfo.module.scss';
import { modalTypes } from '../common/modals/constants';

interface FeaturedLoungeWaitListInfoProps {
  loungeId: string;
}

const FeaturedLoungeWaitListInfo: React.FC<FeaturedLoungeWaitListInfoProps> = ({
  loungeId,
}) => {
  const {
    lounges: { viewLounge },
  } = useCommonContent();
  const { customTrack } = useAnalyticsContext();
  const { locale, product } = useRouteParams() as RouteParams;
  const { buttonText, instructions, prefix, status, title, url } =
    useUserWaitListDetails(loungeId) || {};
  const isLoggedIn = useSelector(getIsLoggedIn);
  // retrieve product type if product in view is in CM's wallet
  const { product: { type: productTypes = [] } = {}, homeCountryLocale } =
    (useSelectedProductFromUserWallet(product, locale) || {}) as {
      product?: { type?: string[] };
      homeCountryLocale?: string;
    };
  const { setModalName } = useModal();
  const router = useRouter();
  const lounge = useLoungeById(loungeId);
  const isWaitListAvailableAtLounge = useIsWaitListEnabledLounge(loungeId);

  if (!isWaitListAvailableAtLounge) {
    return null;
  }

  const loungeTerminal = lounge?.location?.terminal || '';
  const loungePath = lounge
    ? loungeHelper.generateLoungeUrl({
        locale,
        loungeId: lounge.id,
        loungeName: lounge.name,
        loungeTerminal,
      })
    : '';

  // use card to determine locale when logged in
  const isUSProduct = isLoggedIn
    ? homeCountryLocale === locales.UnitedStates
    : locale === locales.UnitedStates.toLocaleLowerCase();
  const userHasEligibleProduct = centurionLoungeEligibleCards.some(
    (eligibleCard) => productTypes.includes(eligibleCard) && isUSProduct
  );
  const shouldHideWaitlistLink = !buttonText || !userHasEligibleProduct;

  // Display link for only US products
  const shouldHideLink = isLoggedIn ? shouldHideWaitlistLink : !isUSProduct;

  if (shouldHideLink) {
    return null;
  }

  const loginStateText = isLoggedIn ? 'logged-in' : 'logged-out';

  const clickHandler = (event: React.MouseEvent<HTMLElement>) => {
    customTrack({
      event,
      detail: `${buttonText}`,
      element: `${loginStateText} WaitList Button`,
    });

    saveScrollBeforeExternalNavigation({
      storageKey: WAITLIST_SCROLL_POSITION_KEY,
    });
  };

  const viewLoungeClickHandler = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    customTrack({
      event,
      detail: `${viewLounge}`,
      element: `View Lounge Button`,
    });
    saveScrollBeforeExternalNavigation({
      storageKey: WAITLIST_SCROLL_POSITION_KEY,
    });
    const basePath = getBasePathFromLocation(locale);
    router.push({
      pathname: addTrailingSlashIfMissing(
        `${basePath}/${product}/${lounge?.airport?.iata}/${loungePath}`
      ),
    });
  };

  const shouldOpenModal = [waitlistStatus.GET, waitlistStatus.JOIN].includes(
    status as string
  );
  const displayEntryPoint = () => {
    if (shouldOpenModal) {
      return (
        <Button
          className="tw-w-full tw-max-w-300"
          variant="primary"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setModalName(modalTypes.BEFORE_YOU_GO_CONFIRMATION_MODAL);
            clickHandler(e);
          }}
          reactlytics={{ disable: true }}
        >
          {buttonText}
        </Button>
      );
    }

    return (
      <Link
        className={cn(
          'btn primary btn-primary flex-justify-center dls-white tw-w-full tw-max-w-300',
          styles.waitListButton
        )}
        href={url}
        isInline
        onClick={(e) => {
          e.stopPropagation();
          clickHandler(e);
        }}
        reactlytics={{ disable: true }}
        target="_blank"
      >
        {buttonText}
      </Link>
    );
  };

  return (
    <div
      className={featuredStyles.isolateFromParentLink}
      // Prevents clicks on non-actionable areas (text, dividers, whitespace) from bubbling up
      // to the parent LFLink, which would otherwise trigger navigation.
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {/* Mobile View */}
      <hr className="tw-border-gray-300 md_tw-hidden" />
      <div className="tw-m-4 tw-flex tw-justify-between tw-gap-2 md_tw-hidden">
        <div className="tw-text-sm">
          {prefix && <p className="tw-text-gray-800">{prefix}</p>}
          <p className="tw-font-bold tw-text-gray-900">{title}</p>
        </div>
        <div>{displayEntryPoint()}</div>
      </div>

      {/* Desktop View */}
      <div className="tw-hidden tw-items-center tw-py-3 md_tw-flex">
        <div
          className={cn(featuredStyles.dividerFadeRight, 'tw-h-px tw-flex-1')}
        />
        <IconTime className="tw-mx-1 tw-text-blue-bright-600" size="md" />
        <div
          className={cn(featuredStyles.dividerFadeLeft, 'tw-h-px tw-flex-1')}
        />
      </div>
      <div className="tw-hidden md_tw-mx-6 md_tw-mb-6 md_tw-block xl_tw-mx-8 xl_tw-mb-8">
        {instructions && (
          <div>
            <h3 className="tw-text-center tw-text-base tw-text-gray-900">
              {prefix && <span>{`${prefix} `}</span>}
              <span className="tw-font-bold">{title}</span>
            </h3>
            <div className="tw-mt-1 tw-text-center tw-text-xs tw-text-gray-700">
              {instructions}
            </div>
          </div>
        )}
        <div className="tw-mt-4 tw-flex tw-gap-4">
          <Button
            className="tw-w-full tw-max-w-300"
            variant="secondary"
            onClick={viewLoungeClickHandler}
            reactlytics={{ disable: true }}
          >
            {viewLounge}
          </Button>
          {displayEntryPoint()}
        </div>
      </div>
    </div>
  );
};

export default FeaturedLoungeWaitListInfo;
